import { useState, useRef, useEffect } from 'react';

export default function B2BSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [submitState, setSubmitState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [hoveredBenefit, setHoveredBenefit] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitState('loading');
    const form = e.currentTarget;
    const formData = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      formData.append(key, value.toString());
    });
    try {
      const res = await fetch('https://readdy.ai/api/form/d7bqhkofgr5j5eoavc5g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      if (res.ok) {
        form.reset();
        setSubmitState('success');
      } else {
        setSubmitState('error');
      }
    } catch {
      setSubmitState('error');
    }
  };

  const benefits = [
    { icon: 'ri-heart-line', title: 'Benefit, který zaměstnanci ocení', desc: 'Ušetří čas i starosti — oblečení vyžehlíme za ně' },
    { icon: 'ri-truck-line', title: 'Svoz přímo z kanceláře', desc: 'Přijedeme k vám, zaměstnanci nemusí nikam chodit' },
    { icon: 'ri-shake-hands-line', title: 'Individuální firemní nabídka', desc: 'Cenu nastavíme osobně podle vašich konkrétních potřeb' },
    { icon: 'ri-calendar-check-line', title: 'Pravidelný harmonogram', desc: 'Nastavíme opakující se svoz přesně dle vašich potřeb' },
  ];

  const stats = [
    { value: '24h', label: 'odpověď na poptávku' },
    { value: 'Bez závazků', label: 'individuální nabídka' },
  ];

  return (
    <section ref={sectionRef} className="py-16 md:py-24 bg-gray-50 dark:bg-gray-900 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-6">

        {/* Nadpis */}
        <div
          className="text-center mb-12 md:mb-16"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(32px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <div className="inline-flex items-center gap-2 bg-teal-100 dark:bg-teal-900/40 px-4 py-2 rounded-full mb-4">
            <i className="ri-building-2-line text-teal-600 dark:text-teal-400 text-sm"></i>
            <span className="text-sm font-semibold text-teal-700 dark:text-teal-300">Pro firmy a zaměstnavatele</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4 leading-tight">
            Žehlení jako benefit<br />
            <span className="text-teal-600 dark:text-teal-400">pro vaše zaměstnance</span>
          </h2>
          <p className="text-base md:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Nabídněte svým lidem benefit, který ocení každý den. Profesionální žehlení
            košil a pracovního oblečení — svoz přímo z kanceláře nebo od zaměstnance domů.
          </p>
        </div>

        {/* Stats bar */}
        <div
          className="grid grid-cols-2 gap-4 mb-12 max-w-sm mx-auto"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.6s ease 0.2s, transform 0.6s ease 0.2s',
          }}
        >
          {stats.map((stat, i) => (
            <div
              key={i}
              className="text-center bg-white dark:bg-gray-800 rounded-xl py-4 px-2 border border-gray-100 dark:border-gray-700"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'scale(1)' : 'scale(0.85)',
                transition: `opacity 0.5s ease ${0.3 + i * 0.1}s, transform 0.5s ease ${0.3 + i * 0.1}s`,
              }}
            >
              <p className="text-2xl font-bold text-teal-600 dark:text-teal-400">{stat.value}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{stat.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-start">

          {/* Levá strana */}
          <div
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateX(0)' : 'translateX(-40px)',
              transition: 'opacity 0.7s ease 0.3s, transform 0.7s ease 0.3s',
            }}
          >
            {/* Fotka s parallax overlay efektem */}
            <div className="relative rounded-2xl overflow-hidden mb-8 h-56 md:h-72 w-full group">
              <img
                src="https://storage.readdy-site.link/project_files/2eceecbc-653f-4c60-a25c-c17e848b9862/ed501f53-51e7-449e-b9d7-1b71ac5624a9_WoM2fbRhR8Mo8s1FIhwx8_SqrQbn05_00001.jpg?v=4ce08497dec97cc67d77e4b553e918ce"
                alt="Žehlení jako benefit pro zaměstnance"
                className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent"></div>


            </div>

            {/* Benefity s hover efektem */}
            <div className="space-y-3">
              {benefits.map((item, i) => (
                <div
                  key={i}
                  onMouseEnter={() => setHoveredBenefit(i)}
                  onMouseLeave={() => setHoveredBenefit(null)}
                  className="flex items-start gap-4 p-3 rounded-xl cursor-default transition-all duration-300"
                  style={{
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'translateX(0)' : 'translateX(-24px)',
                    transition: `opacity 0.5s ease ${0.5 + i * 0.1}s, transform 0.5s ease ${0.5 + i * 0.1}s, background 0.2s ease`,
                    background: hoveredBenefit === i ? 'rgba(20,184,166,0.08)' : 'transparent',
                  }}
                >
                  <div
                    className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 transition-all duration-300"
                    style={{
                      background: hoveredBenefit === i ? 'rgb(20,184,166)' : 'rgb(204,251,241)',
                    }}
                  >
                    <i
                      className={`${item.icon} text-lg transition-colors duration-300`}
                      style={{ color: hoveredBenefit === i ? 'white' : 'rgb(13,148,136)' }}
                    ></i>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white text-sm md:text-base">{item.title}</p>
                    <p className="text-gray-500 dark:text-gray-400 text-sm">{item.desc}</p>
                  </div>
                  <div
                    className="ml-auto flex items-center justify-center w-5 h-5 transition-all duration-300"
                    style={{
                      opacity: hoveredBenefit === i ? 1 : 0,
                      transform: hoveredBenefit === i ? 'translateX(0)' : 'translateX(-6px)',
                    }}
                  >
                    <i className="ri-arrow-right-line text-teal-500 text-sm"></i>
                  </div>
                </div>
              ))}
            </div>

            {/* Tip box */}
            <div
              className="mt-6 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-700 rounded-xl p-4"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(16px)',
                transition: 'opacity 0.6s ease 0.9s, transform 0.6s ease 0.9s',
              }}
            >
              <p className="text-sm text-teal-800 dark:text-teal-300 flex items-start gap-2">
                <i className="ri-lightbulb-line text-teal-600 dark:text-teal-400 mt-0.5 flex-shrink-0"></i>
                <span>
                  <strong>Tip pro HR:</strong> Žehlení jako zaměstnanecký benefit zvyšuje
                  spokojenost a loajalitu zaměstnanců. Kontaktujte nás pro individuální nabídku.
                </span>
              </p>
            </div>
          </div>

          {/* Pravá strana - formulář */}
          <div
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateX(0)' : 'translateX(40px)',
              transition: 'opacity 0.7s ease 0.4s, transform 0.7s ease 0.4s',
            }}
          >
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 md:p-8 border border-gray-100 dark:border-gray-700 transition-shadow duration-300 hover:shadow-lg hover:shadow-teal-100/50 dark:hover:shadow-teal-900/20">
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-1">
                Nezávazná poptávka
              </h3>
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
                Napište nám a do 24 hodin připravíme individuální nabídku.
              </p>

              {submitState === 'success' ? (
                <div className="text-center py-10">
                  <div
                    className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4"
                    style={{ animation: 'scaleIn 0.4s ease' }}
                  >
                    <i className="ri-check-line text-teal-600 text-3xl"></i>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Poptávka odeslána!</h4>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">Ozveme se vám do 24 hodin.</p>
                  <button
                    onClick={() => setSubmitState('idle')}
                    className="mt-4 text-teal-600 text-sm underline cursor-pointer"
                  >
                    Odeslat další poptávku
                  </button>
                </div>
              ) : (
                <form
                  data-readdy-form
                  id="b2b-homepage-form"
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Jméno a příjmení *</label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="Jana Nováková"
                        className="w-full border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100 dark:focus:ring-teal-900/30 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Název firmy *</label>
                      <input
                        type="text"
                        name="company"
                        required
                        placeholder="Vaše firma s.r.o."
                        className="w-full border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100 dark:focus:ring-teal-900/30 transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">E-mail *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="jana@firma.cz"
                      className="w-full border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100 dark:focus:ring-teal-900/30 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Telefon</label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="+420 123 456 789"
                      className="w-full border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100 dark:focus:ring-teal-900/30 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Počet zaměstnanců</label>
                    <select
                      name="employees"
                      className="w-full border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100 dark:focus:ring-teal-900/30 transition-all bg-white cursor-pointer"
                    >
                      <option value="">Vyberte...</option>
                      <option value="1-10">1–10 zaměstnanců</option>
                      <option value="11-50">11–50 zaměstnanců</option>
                      <option value="51-200">51–200 zaměstnanců</option>
                      <option value="200+">200+ zaměstnanců</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Zpráva</label>
                    <textarea
                      name="message"
                      rows={3}
                      maxLength={500}
                      placeholder="Popište vaše potřeby — typ oblečení, frekvence, lokalita..."
                      className="w-full border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100 dark:focus:ring-teal-900/30 transition-all resize-none"
                    />
                  </div>

                  {submitState === 'error' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                      <p className="text-red-700 text-sm">Nepodařilo se odeslat. Zkuste to prosím znovu.</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitState === 'loading'}
                    className="relative w-full bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white font-semibold py-3 rounded-lg text-sm whitespace-nowrap cursor-pointer overflow-hidden group transition-colors duration-200"
                  >
                    <span
                      className="absolute inset-0 bg-white/10 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-500 skew-x-12"
                    ></span>
                    <span className="relative flex items-center justify-center gap-2">
                      {submitState === 'loading' ? (
                        <>
                          <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin inline-block"></span>
                          Odesílám...
                        </>
                      ) : (
                        <>
                          Odeslat poptávku
                          <i className="ri-send-plane-line text-base transition-transform duration-200 group-hover:translate-x-1"></i>
                        </>
                      )}
                    </span>
                  </button>

                  <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
                    Bez závazků. Odpovídáme do 24 hodin.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes scaleIn {
          from { transform: scale(0.5); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </section>
  );
}
